import classNames from 'classnames'
import {
  Children,
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { getAccessibilityFunction, getAccessibilityTag } from '../../config'
import { IconButton, IconButtonSize, IconButtonVariants } from '../button'
import { ChevronBackwardIcon, ChevronForwardIcon } from '../icon'
import styles from './Carousel.module.scss'
import type { CarouselHandle, CarouselProps } from './types'

const Carousel = forwardRef(function Carousel(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-live': ariaLive = 'polite',
    'aria-roledescription': ariaRoleDescription = 'carousel',
    className,
    children,
    getSlideAriaLabel,
    nextButtonAriaLabel,
    onNext,
    onPrevious,
    previousButtonAriaLabel,
    role = 'group',
    withButtons = true,
  }: CarouselProps,
  ref?: ForwardedRef<unknown>
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showButtons, setShowButtons] = useState(false)
  const nextAriaLabel = getAccessibilityTag('next')
  const previousAriaLabel = getAccessibilityTag('previous')
  const getSlideOfTotal = getAccessibilityFunction('getSlideOfTotal')

  const numberOfSlides = Children.count(children)
  const [showPrevButton, setShowPrevButton] = useState(false)
  const [showNextButton, setShowNextButton] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      const hasOverflow = container.scrollWidth > container.clientWidth
      setShowButtons(withButtons && hasOverflow)
    }
  }, [children, withButtons])

  const handlePrevButtonClick = useCallback(() => {
    const newIndex = scroll('left')
    onPrevious?.(newIndex)
  }, [onPrevious])

  const handleNextButtonClick = useCallback(() => {
    const newIndex = scroll('right')
    onNext?.(newIndex)
  }, [onNext])

  useImperativeHandle(
    ref,
    (): CarouselHandle => ({
      scrollPrevious() {
        handlePrevButtonClick()
      },
      scrollNext() {
        handleNextButtonClick()
      },
      scrollToIndex(index: number) {
        scrollToIndex(index)
      },
    }),
    [handlePrevButtonClick, handleNextButtonClick]
  )

  const scrollToIndex = (index: number) => {
    const container = containerRef.current
    if (!container) {
      return
    }
    const childrenEls = Array.from(container.children) as HTMLDivElement[]
    const el = childrenEls[index]
    if (el) {
      container.scrollTo({ left: el.offsetLeft, behavior: 'smooth' })
    }
  }

  const scroll = (direction: 'left' | 'right'): number | undefined => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const childrenEls = Array.from(container.children) as HTMLDivElement[]
    const scrollLeft = container.scrollLeft

    if (direction === 'right') {
      for (let i = 0; i <= childrenEls.length - 1; i++) {
        const el = childrenEls[i]
        if (el.offsetLeft > scrollLeft + 1) {
          container.scrollTo({ left: el.offsetLeft, behavior: 'smooth' })
          return i
        }
      }
    } else {
      for (let i = childrenEls.length - 1; i >= 0; i--) {
        const el = childrenEls[i]
        if (el.offsetLeft < scrollLeft - 1) {
          container.scrollTo({ left: el.offsetLeft, behavior: 'smooth' })
          return i
        }
      }
    }
  }

  const computeButtonsVisibility = useCallback(() => {
    if (!containerRef.current) {
      return
    }
    const canScrollPrev = containerRef.current.scrollLeft > 0
    const canScrollRight =
      containerRef.current.scrollLeft <
      containerRef.current.scrollWidth - containerRef.current.clientWidth
    setShowPrevButton(canScrollPrev || false)
    setShowNextButton(canScrollRight || false)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    computeButtonsVisibility()
    container?.addEventListener('scroll', computeButtonsVisibility)
    container?.addEventListener('resize', computeButtonsVisibility)
    return () => {
      container?.removeEventListener('scroll', computeButtonsVisibility)
      container?.removeEventListener('resize', computeButtonsVisibility)
    }
  }, [computeButtonsVisibility])

  return (
    <section className={styles['pintxo-carousel']}>
      <div
        className={classNames(styles['pintxo-carousel__wrapper'], className)}
        aria-roledescription={ariaRoleDescription}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        <div
          aria-roledescription="carousel"
          aria-live={ariaLive}
          className={classNames(styles['pintxo-carousel__wrapper__container'])}
          ref={containerRef}
        >
          {Children.map(children, (child, childIndex) => (
            <div
              aria-label={
                (getSlideAriaLabel && getSlideAriaLabel(childIndex, numberOfSlides)) ||
                getSlideOfTotal(childIndex, numberOfSlides)
              }
              aria-roledescription="slide"
              className={classNames(styles['pintxo-carousel__wrapper__container__element'])}
              role={role}
            >
              {child}
            </div>
          ))}
        </div>
        {showButtons && (
          <div className={styles['pintxo-carousel__buttons']}>
            <div className={styles['pintxo-carousel__buttons__button-left']}>
              {showPrevButton && (
                <IconButton
                  onClick={handlePrevButtonClick}
                  icon={ChevronBackwardIcon}
                  variant={IconButtonVariants.floating}
                  size={IconButtonSize.small}
                  aria-label={previousButtonAriaLabel || previousAriaLabel}
                />
              )}
            </div>
            <div className={styles['pintxo-carousel__buttons__button-right']}>
              {showNextButton && (
                <IconButton
                  onClick={handleNextButtonClick}
                  icon={ChevronForwardIcon}
                  variant={IconButtonVariants.floating}
                  size={IconButtonSize.small}
                  aria-label={nextButtonAriaLabel || nextAriaLabel}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
})

export default Carousel
